import {
  Close as CloseIcon,
} from '../icons';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PaymentSuccessCard from './PaymentSuccessCard';
import PaymentFailedCard from './PaymentFailedCard';

const PaymentStatusDialog = ({
  open,
  onClose,
  paymentStatus,
  paymentId,
  orderId,
  onRetryPayment
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && paymentId && orderId) {
      // Reset state when dialog opens
      setLoading(true);
      setError(null);
      setPayment(null);
      setOrder(null);

      // Validate UUIDs before making API calls
      try {
        // Simple UUID validation (basic format check)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

        if (!uuidRegex.test(paymentId) || !uuidRegex.test(orderId)) {
          console.error('Invalid UUID format:', { paymentId, orderId });
          setError('Invalid payment or order ID format');
          setLoading(false);
          return;
        }

        fetchPaymentAndOrder();
      } catch (err) {
        console.error('UUID validation error:', err);
        setError('Invalid payment or order ID format');
        setLoading(false);
      }
    } else if (!open) {
      // Reset state when dialog closes
      setLoading(false);
      setError(null);
      setPayment(null);
      setOrder(null);
    }
  }, [open, paymentId, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentAndOrder = async () => {
    setError(null);

    console.log('🔍 PaymentStatusDialog: Fetching payment and order details', { paymentId, orderId });

    try {
      // Fetch payment and order details using public endpoints (no auth required)
      const [paymentData, orderData] = await Promise.all([
        api.get(`/payments/public/${paymentId}/details/`).then(response => response.data),
        api.get(`/orders/public/${orderId}/status/`).then(response => response.data)
      ]);

      console.log('🔍 PaymentStatusDialog: Payment data received:', paymentData);
      console.log('🔍 PaymentStatusDialog: Order data received:', orderData);

      // Set default values if data is missing and normalize data types
      const normalizedPayment = paymentData || {};
      const normalizedOrder = orderData || {};

      // Ensure amounts are numbers (public API returns strings)
      if (normalizedPayment.amount && typeof normalizedPayment.amount === 'string') {
        normalizedPayment.amount = parseFloat(normalizedPayment.amount) || 0;
      }
      if (normalizedOrder.total && typeof normalizedOrder.total === 'string') {
        normalizedOrder.total = parseFloat(normalizedOrder.total) || 0;
      }

      // Normalize order items prices
      if (normalizedOrder.items && Array.isArray(normalizedOrder.items)) {
        normalizedOrder.items = normalizedOrder.items.map(item => ({
          ...item,
          price: typeof item.price === 'string' ? parseFloat(item.price) || 0 : (item.price || 0),
          total: typeof item.total === 'string' ? parseFloat(item.total) || 0 : (item.total || 0),
          quantity: typeof item.quantity === 'string' ? parseInt(item.quantity) || 1 : (item.quantity || 1)
        }));
      }

      setPayment(normalizedPayment);
      setOrder(normalizedOrder);

      console.log('🔍 PaymentStatusDialog: Successfully loaded data', {
        payment: normalizedPayment,
        order: normalizedOrder
      });

    } catch (err) {
      console.error('🔍 PaymentStatusDialog: Error fetching payment/order:', err);

      // Handle different types of API errors
      let errorMessage = 'Failed to load payment details';

      if (err.response) {
        // API responded with error status
        if (err.response.status === 404) {
          errorMessage = 'Payment or order not found. It may still be processing or the payment link may be expired.';
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = err.response.data?.detail || err.response.data?.error || `API error (${err.response.status})`;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = err.message || 'Failed to load payment details';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleRetryPayment = () => {
    if (onRetryPayment) {
      onRetryPayment(order, payment);
    }
    onClose();
  };

  const handleTrackOrder = () => {
    onClose();
    if (order?.order_number) {
      // Navigate back to menu page with order tracking parameters
      const currentUrl = new URL(window.location);
      const merchantParam = currentUrl.searchParams.get('merchant') ? `merchant=${currentUrl.searchParams.get('merchant')}` : '';
      const tableParam = currentUrl.searchParams.get('table') ? `&table=${currentUrl.searchParams.get('table')}` : '';
      navigate(`/menu?${merchantParam}${tableParam}&track_order=${order.order_number}`);
    }
  };

  const handleBackToMenu = () => {
    onClose();
    // Stay on menu page - just close dialog
  };


  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              mb: 3,
              color: 'primary.main'
            }}
          />
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            Loading Payment Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch your payment information...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      );
    }

    if (paymentStatus === 'success') {
      return (
        <PaymentSuccessCard
          payment={payment}
          order={order}
          countdown={0}
          isRedirecting={false}
          onCancelRedirect={() => {}}
          onGoToMenu={handleBackToMenu}
          onGoToOrderTracking={handleTrackOrder}
          onPrintReceipt={() => {}}
          onEmailReceipt={() => {}}
        />
      );
    } else {
      return (
        <PaymentFailedCard
          payment={payment}
          order={order}
          onRetryPayment={handleRetryPayment}
          onContactSupport={() => {}}
          onGoToMenu={handleBackToMenu}
        />
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatusDialog;

